import React from 'react';
import { InventoryAdminListItem } from '../../types';

interface InventoryListProps {
  inventories: InventoryAdminListItem[];
  loading: boolean;
  statusFilter?: number;
  onStatusFilterChange: (status: number | undefined) => void;
  variantFilter?: string;
  onVariantFilterChange: (variant: string | undefined) => void;
  onEdit: (inventory: InventoryAdminListItem) => void;
  onEditImages: (inventory: InventoryAdminListItem) => void;
  onDuplicate: (inventory: InventoryAdminListItem) => void;
  onActivate: (inventoryId: string) => void;
  onDeactivate: (inventoryId: string) => void;
  onPublish: (inventoryId: string) => void;
  onSchedule: (inventory: InventoryAdminListItem) => void;
  onManageSlots: (inventory: InventoryAdminListItem) => void;
  getStatusBadge: (status: number) => React.ReactNode;
}

export const InventoryList: React.FC<InventoryListProps> = ({
  inventories,
  loading,
  statusFilter,
  onStatusFilterChange,
  variantFilter,
  onVariantFilterChange,
  onEdit,
  onEditImages,
  onDuplicate,
  onActivate,
  onDeactivate,
  onPublish,
  onSchedule,
  onManageSlots,
  getStatusBadge,
}) => {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-gray-600 dark:text-gray-400">Loading inventories...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center space-x-2">
              <label htmlFor="status-filter" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Status:
              </label>
              <select
                id="status-filter"
                value={statusFilter !== undefined ? statusFilter : ''}
                onChange={(e) => onStatusFilterChange(e.target.value === '' ? undefined : parseInt(e.target.value))}
                className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
              >
                <option value="">All Status</option>
                <option value="0">DRAFT</option>
                <option value="1">LIVE</option>
                <option value="2">DEACTIVATED</option>
              </select>
            </div>

            <div className="flex items-center space-x-2">
              <label htmlFor="variant-filter" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Variant:
              </label>
              <select
                id="variant-filter"
                value={variantFilter || ''}
                onChange={(e) => onVariantFilterChange(e.target.value === '' ? undefined : e.target.value)}
                className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
              >
                <option value="">All Variants</option>
                <option value="FIT">FIT</option>
                <option value="GIT">GIT</option>
              </select>
            </div>
          </div>
          <div className="text-sm text-gray-500 dark:text-gray-400">
            {inventories.length} inventory{inventories.length !== 1 ? 'ies' : ''}
          </div>
        </div>
      </div>

      {/* Inventory Grid */}
      {inventories.length === 0 ? (
        <div className="text-center py-12">
          <div className="text-gray-400 dark:text-gray-500 mb-4">
            <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No inventories found</h3>
          <p className="text-gray-500 dark:text-gray-400">
            Create your first inventory to get started.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {inventories.map((inventory) => (
            <div
              key={inventory.id}
              className="bg-white dark:bg-gray-800 rounded-lg shadow-md hover:shadow-lg transition-shadow"
            >
              {/* Image */}
              <div className="aspect-w-16 aspect-h-9 rounded-t-lg overflow-hidden">
                {inventory.image ? (
                  <img
                    src={inventory.image}
                    alt={inventory.title}
                    className="w-full h-48 object-cover"
                  />
                ) : (
                  <div className="w-full h-48 bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                    <svg className="h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                )}
              </div>

              {/* Content */}
              <div className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white line-clamp-2">
                    {inventory.title}
                  </h3>
                  {getStatusBadge(inventory.status)}
                </div>

                {inventory.about && (
                  <p className="text-gray-600 dark:text-gray-400 text-sm mb-3 line-clamp-2">
                    {inventory.about}
                  </p>
                )}

                {inventory.duration && (
                  <div className="flex items-center text-sm text-gray-500 dark:text-gray-400 mb-3">
                    <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    {inventory.duration}
                  </div>
                )}

                {/* Actions */}
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => onEdit(inventory)}
                    className="px-3 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors"
                  >
                    Edit
                  </button>
                  
                  <button
                    onClick={() => onEditImages(inventory)}
                    className="px-3 py-1 text-xs bg-indigo-100 text-indigo-700 rounded hover:bg-indigo-200 transition-colors"
                  >
                    Edit Images
                  </button>
                  
                  <button
                    onClick={() => onDuplicate(inventory)}
                    className="px-3 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
                  >
                    Duplicate
                  </button>

                  <button
                    onClick={() => onSchedule(inventory)}
                    className="px-3 py-1 text-xs bg-green-100 text-green-700 rounded hover:bg-green-200 transition-colors"
                  >
                    Schedule
                  </button>

                  <button
                    onClick={() => onManageSlots(inventory)}
                    className="px-3 py-1 text-xs bg-purple-100 text-purple-700 rounded hover:bg-purple-200 transition-colors"
                  >
                    Slots
                  </button>
                </div>

                {/* Status Actions */}
                <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                  <div className="flex flex-wrap gap-2">
                    {inventory.status === 0 && (
                      <button
                        onClick={() => onPublish(inventory.id)}
                        className="px-3 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700 transition-colors"
                      >
                        Publish
                      </button>
                    )}
                    
                    {inventory.status === 1 && (
                      <button
                        onClick={() => onDeactivate(inventory.id)}
                        className="px-3 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
                      >
                        Deactivate
                      </button>
                    )}
                    
                    {inventory.status === 2 && (
                      <button
                        onClick={() => onActivate(inventory.id)}
                        className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                      >
                        Activate
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
